// The three.js adapter for @bebop/forge/solid — macro-form and silhouette.
//
// Same shape of module as render/forge.ts is for the texture core: the
// authoring is engine-agnostic and tested (tools/forge/src/solid.ts), and
// this file does nothing but turn its typed arrays into BufferGeometry and
// cache the ones that get asked for repeatedly.
//
// WHY IT MATTERS, in one line: a normal map cannot move a silhouette. Every
// judge across the last two waves has named some version of that — "flat
// planes wearing pictures", "untextured mass", "boxes on toy wheels" — and
// every previous answer has been another texture. These are shapes.

import {
  type AdobeOptions,
  adobeShell,
  catenary,
  chamferBox,
  type RockOptions,
  reliefRock,
  type Solid,
} from "@bebop/forge/solid";
import * as THREE from "three";

function toGeometry(s: Solid): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(s.position, 3));
  g.setAttribute("normal", new THREE.BufferAttribute(s.normal, 3));
  g.setAttribute("uv", new THREE.BufferAttribute(s.uv, 2));
  g.setIndex(new THREE.BufferAttribute(s.index, 1));
  g.computeBoundingSphere();
  return g;
}

/**
 * THE UNIT CHAMFERED BOX — the one to reach for when replacing a
 * `BoxGeometry(1,1,1)` behind an InstancedMesh.
 *
 * The chamfer is in UNIT space, so a non-uniform instance scale makes it
 * anisotropic: a 0.03 chamfer on a 20 x 60 x 18 m tower is 0.6 m across the
 * plan and 1.8 m up the height. That is not a defect to apologise for — a
 * real cornice IS deeper than the reveal beside it — but it does mean the
 * value wants to be small, and that callers with wildly non-cubic scales
 * should reach for `chamferGeometry` with the real dimensions instead.
 *
 * Cached by chamfer size: the whole city asks for two of these.
 */
const unitCache = new Map<number, THREE.BufferGeometry>();
export function unitChamferBox(chamfer = 0.028): THREE.BufferGeometry {
  const key = Math.round(chamfer * 10000);
  const hit = unitCache.get(key);
  if (hit) return hit;
  const g = toGeometry(chamferBox(1, 1, 1, chamfer));
  unitCache.set(key, g);
  return g;
}

/** A chamfered box at real dimensions, origin at its centre. */
export function chamferGeometry(
  w: number,
  h: number,
  d: number,
  chamfer: number,
): THREE.BufferGeometry {
  return toGeometry(chamferBox(w, h, d, chamfer));
}

/**
 * A doubly-curved adobe volume standing on y = 0.
 *
 * Replaces `BoxGeometry(w, h, d)` for anything built out of mud: the
 * settlement's shopfronts, its stores, its outbuildings. See the generator
 * for why each of batter/bulge/wobble/sag is in there.
 */
export function adobeGeometry(
  w: number,
  h: number,
  d: number,
  seed: number,
  opts?: AdobeOptions,
): THREE.BufferGeometry {
  return toGeometry(adobeShell(w, h, d, seed, opts));
}

/** A boulder with genuine relief and undercuts, centred on its own origin. */
export function rockGeometry(
  radius: number,
  seed: number,
  opts?: RockOptions,
): THREE.BufferGeometry {
  return toGeometry(reliefRock(radius, seed, opts));
}

/** A cable hanging in a true catenary between two world points. */
export function catenaryGeometry(
  a: THREE.Vector3 | { x: number; y: number; z: number },
  b: THREE.Vector3 | { x: number; y: number; z: number },
  sag: number,
  radius: number,
  steps?: number,
): THREE.BufferGeometry {
  return toGeometry(catenary(a.x, a.y, a.z, b.x, b.y, b.z, sag, radius, steps));
}
